import type { Profile } from '@liff/get-profile'
import liff from '@line/liff'
import * as Sentry from '@sentry/react'
import { signInWithCustomToken } from 'firebase/auth'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'

import { env } from '@/config'
import { auth } from '@/lib/firebase'

import { getApiAuthToken } from '../api/client'
import { LINE_ENDPOINTS, lineClient } from './http'

interface JWTPayload {
  iss?: string
  sub?: string
  aud?: string
  exp?: number
  iat?: number
  auth_time?: number
  nonce?: string
  amr?: string[]
  name?: string
  picture?: string
  email?: string
}

export type LIFFContextType = {
  isReady: boolean
  isLoggedIn: boolean
  isInClient: boolean
  profile: Profile | null
  isFriend: boolean
  requestFriendship: () => Promise<void>
  decodedIdToken: JWTPayload | null
}

const DefaultLIFFContext: LIFFContextType = {
  isReady: false,
  isLoggedIn: false,
  isInClient: false,
  profile: null,
  isFriend: false,
  requestFriendship: () => Promise.resolve(),
  decodedIdToken: null,
}

export const LIFFContext = createContext(DefaultLIFFContext)

export type UseLIFFContextOptions = {
  liffId: string
  withLogin?: boolean
}

export const useLIFFContext = ({
  liffId,
  withLogin = true,
}: UseLIFFContextOptions): LIFFContextType => {
  const isInit = useRef(false)

  const [isReady, setIsReady] = useState(false)
  const [isLoggedIn, setIsLoggedIn] = useState(false)
  const [isInClient, setIsInClient] = useState(false)
  const [profile, setProfile] = useState<Profile | null>(null)
  const [decodedIdToken, setDecodedIdToken] = useState<JWTPayload | null>(null)

  const [isFriend, setIsFriend] = useState(false)

  const initializeLIFF = useCallback(
    async (liffId: string) => {
      // 1. Safety Timeout: Force isReady to true after 10 seconds to avoid permanent loading
      const safetyTimeout = setTimeout(() => {
        if (!isReady) {
          console.warn('LIFF Initialization timed out. Forcing ready state.')
          setIsReady(true)
        }
      }, 10000)

      try {
        await liff.init({ liffId })

        const isLoggedInQuery = liff.isLoggedIn()
        setIsLoggedIn(isLoggedInQuery)

        const isInClientQuery = liff.isInClient()
        setIsInClient(isInClientQuery)

        const os = liff.getOS()
        if (os) {
          document.documentElement.setAttribute('data-os', os)
        }

        // Handle external mobile browser redirect
        if (!isInClientQuery && os !== 'web') {
          const path = window.location.href.replace(window.location.origin, '')
          const lineScheme = `line://app/${env.VITE_LIFF_ID}?liff.state=${encodeURIComponent(path)}`
          window.location.replace(lineScheme)
          setTimeout(() => {
            window.close()
          }, 5000)
          return
        }

        // Automatic login if required
        if (withLogin && !isLoggedInQuery) {
          return liff.login({
            redirectUri: window.location.href,
          })
        }

        if (isLoggedInQuery) {
          // Parallel fetch of critical and non-critical data
          // We use individual try-catches to ensure one failure doesn't block the app
          try {
            const [profileRes, friendshipRes, tokenRes] =
              await Promise.allSettled([
                lineClient.get(LINE_ENDPOINTS.profile).json<Profile>(),
                lineClient
                  .get(LINE_ENDPOINTS.friendship)
                  .json<{ friendFlag: boolean }>(),
                getApiAuthToken(),
              ])

            // Handle Profile
            if (profileRes.status === 'fulfilled') {
              const profileQuery = profileRes.value
              setProfile(profileQuery)

              // Set Sentry user
              Sentry.setUser({
                id: profileQuery.userId,
                username: profileQuery.displayName,
              })
            } else {
              console.error('Failed to fetch profile:', profileRes.reason)
            }

            // Handle Friendship
            if (friendshipRes.status === 'fulfilled') {
              setIsFriend(friendshipRes.value.friendFlag)
            }

            // Handle Firebase Custom Token
            if (tokenRes.status === 'fulfilled') {
              const { customToken } = tokenRes.value.data
              try {
                console.log('Signing in with custom token')
                await signInWithCustomToken(auth, customToken)
                console.log('Firebase Auth Initialized Successfully!')
              } catch (error) {
                console.error('Firebase Auth Sign-in Failed:', error)
              }
            } else {
              console.error(
                'Failed to fetch Firebase Custom Token:',
                tokenRes.reason,
              )
            }

            const decodedIdToken = liff.getDecodedIDToken()
            if (decodedIdToken) {
              setDecodedIdToken(decodedIdToken)
              // Update Sentry user with email if available
              Sentry.setUser({ email: decodedIdToken.email })
            }
          } catch (error) {
            console.error('Data Fetching or Token Exchange Failed:', error)
          }
        }

        console.log('LIFF Initialized Successfully!')
      } catch (error) {
        console.error('LIFF Init Error:', error)
      } finally {
        clearTimeout(safetyTimeout)
        setIsReady(true)
      }
    },
    [withLogin, isReady],
  )

  const requestFriendship = useCallback(async () => {
    try {
      const friendship = await liff.getFriendship()
      setIsFriend(friendship.friendFlag)

      if (friendship.friendFlag) {
        return
      }

      await liff.requestFriendship()

      const newFriendship = await liff.getFriendship()
      setIsFriend(newFriendship.friendFlag)

      if (newFriendship.friendFlag) {
        return
      }
    } catch (error) {
      console.log(error)
    }
  }, [])

  useEffect(() => {
    if (isInit.current) {
      return
    }

    isInit.current = true

    if (!liffId) {
      return
    }

    initializeLIFF(liffId)
  }, [liffId, initializeLIFF])

  return {
    isReady,
    isLoggedIn,
    isInClient,
    profile,
    isFriend,
    requestFriendship,
    decodedIdToken,
  }
}

export const useLIFF = () => {
  const context = useContext(LIFFContext)
  if (!context) {
    throw new Error('useLIFF must be used within LIFFProvider')
  }
  return context
}
